import { useCallback, useEffect, useState } from "react";
import { Gift, Loader2, MessageSquare, Ticket, Trophy, User } from "lucide-react";
import PublicSiteLayout, { PageHero, PublicLinkButton, PUBLIC_LINKS } from "../components/PublicSiteLayout";
import { api } from "../lib/api";
import { getViewerSession } from "../lib/viewerSession";

export default function PublicGiveaways() {
  const [session, setSession] = useState(null);
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enteringId, setEnteringId] = useState("");
  const [entryCounts, setEntryCounts] = useState({});
  const [giveawayTokens, setGiveawayTokens] = useState(null);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokensError, setTokensError] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadGiveaways = useCallback(async () => {
    setLoading(true);
    setError("");
    const { session: viewerSession } = await getViewerSession();
    setSession(viewerSession);
    setGiveawayTokens(null);
    setTokensError("");
    try {
      const { data } = await api.get("/giveaways");
      setGiveaways(data.giveaways || []);
      if (viewerSession) {
        setTokensLoading(true);
        try {
          const balanceData = await fetchViewerBalances(viewerSession);
          setGiveawayTokens(Number(balanceData.balances?.giveaway_currency || 0));
        } catch (balanceErr) {
          setTokensError("Could not load Giveaway Tokens.");
        } finally {
          setTokensLoading(false);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load giveaways right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGiveaways();
  }, [loadGiveaways]);

  const enterGiveaway = async (giveaway) => {
    if (!session) {
      window.location.href = "/login";
      return;
    }
    setEnteringId(giveaway.giveaway_id);
    setError("");
    setMessage("");
    try {
      const count = Math.max(1, Number(entryCounts[giveaway.giveaway_id]) || 1);
      const { data } = await api.post(`/giveaways/${giveaway.giveaway_id}/enter`, { count });
      const updated = data.giveaway;
      setGiveaways((current) => current.map((item) => (
        item.giveaway_id === updated.giveaway_id ? updated : item
      )));
      setMessage(`Added ${data.entries_added || count} ${data.entries_added === 1 ? "entry" : "entries"} for ${updated.title}.`);
      try {
        const balanceData = await fetchViewerBalances(session);
        setGiveawayTokens(Number(balanceData.balances?.giveaway_currency || 0));
      } catch (balanceErr) {
        setTokensError("Could not refresh Giveaway Tokens.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Could not enter this giveaway.");
    } finally {
      setEnteringId("");
    }
  };

  const currentGiveaway = giveaways.find((giveaway) => giveaway.active) || null;
  const recentGiveaway = giveaways.find((giveaway) => !giveaway.active) || null;

  return (
    <PublicSiteLayout>
      <PageHero
        eyebrow="Community giveaways"
        title="Current giveaway."
        body="There is one giveaway open at a time. Sign in with Twitch or Kick when a giveaway is active, review the prizes, and use Giveaway Tokens for extra entries when enabled. Free entry methods may be announced during eligible giveaways."
        actions={(
          <>
            <PublicLinkButton href={session ? "/profile" : "/login?return_to=%2Fgiveaways"} primary>
              <User className="w-4 h-4" /> {session ? "Open profile" : "Sign in to enter"}
            </PublicLinkButton>
            <PublicLinkButton href={PUBLIC_LINKS.discord}>
              <MessageSquare className="w-4 h-4" /> Giveaway updates
            </PublicLinkButton>
          </>
        )}
      />

      {(error || message) && (
        <section className={`mt-8 rounded border px-4 py-3 text-sm ${
          error
            ? "border-red-500/40 bg-red-500/10 text-red-100"
            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
        }`}>
          {error || message}
        </section>
      )}

      <section className="mt-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
          <div>
            <div className="label-eyebrow text-[hsl(var(--currency-event))]">Now open</div>
            <h2 className="text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))] mt-1">The active giveaway</h2>
          </div>
          {!session && (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">
              You can browse while signed out, but Twitch sign-in is required to enter.
            </div>
          )}
        </div>

        {loading ? (
          <div className="surface bg-black/75 p-6 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading giveaways...
          </div>
        ) : !currentGiveaway ? (
          <EmptyGiveaways />
        ) : (
          <GiveawayCard
            giveaway={currentGiveaway}
            session={session}
            entering={enteringId === currentGiveaway.giveaway_id}
            entryCount={entryCounts[currentGiveaway.giveaway_id] || 1}
            giveawayTokens={giveawayTokens}
            tokensLoading={tokensLoading}
            tokensError={tokensError}
            onEntryCountChange={(count) => setEntryCounts((current) => ({ ...current, [currentGiveaway.giveaway_id]: count }))}
            onEnter={() => enterGiveaway(currentGiveaway)}
          />
        )}
      </section>

      {recentGiveaway && (
        <section className="mt-12">
          <div className="label-eyebrow text-[hsl(var(--currency-event))]">Most recent result</div>
          <h2 className="text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))] mt-1 mb-4">Last giveaway</h2>
          <GiveawayCard
            giveaway={recentGiveaway}
            session={session}
            entering={false}
            giveawayTokens={giveawayTokens}
            tokensLoading={tokensLoading}
            tokensError={tokensError}
            onEnter={() => {}}
          />
        </section>
      )}
    </PublicSiteLayout>
  );
}

function GiveawayCard({ giveaway, session, entering, entryCount = 1, giveawayTokens = null, tokensLoading = false, tokensError = "", onEntryCountChange = () => {}, onEnter }) {
  const completed = giveaway.completed || giveaway.status === "completed";
  const entered = giveaway.viewer_entered;
  const disabled = completed || entering;
  const cost = Math.max(0, Number(giveaway.entry_cost_amount) || 0);
  const requestedCount = Math.max(1, Number(entryCount) || 1);
  const totalCost = cost * requestedCount;
  const buttonLabel = completed
    ? "Giveaway completed"
    : session
      ? `Add ${requestedCount} ${requestedCount === 1 ? "entry" : "entries"}`
      : "Sign in to enter";

  return (
    <article className={`surface bg-black/75 p-5 sm:p-6 ${completed ? "opacity-90" : ""}`}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="min-w-0">
          <div className="label-eyebrow text-[hsl(var(--currency-event))]">
            {completed ? "Giveaway completed" : "Current giveaway"}
          </div>
          <h3 className="mt-2 text-3xl sm:text-4xl font-black uppercase tracking-normal leading-tight text-[hsl(var(--foreground))] break-words">{giveaway.title}</h3>
          {giveaway.description && (
            <p className="mt-4 text-base text-[hsl(var(--muted-foreground))] leading-relaxed max-w-3xl">{giveaway.description}</p>
          )}
        </div>
        <div className="shrink-0 inline-flex w-fit items-center gap-2 rounded border border-[hsl(var(--currency-event))]/50 bg-[hsl(var(--currency-event))]/10 px-3 py-2 text-sm text-[hsl(var(--foreground))]">
          <Ticket className="w-4 h-4 text-[hsl(var(--currency-event))]" />
          {giveaway.entry_count || 0} {(giveaway.entry_count || 0) === 1 ? "entry" : "entries"}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] gap-4">
        <PrizePreview prizes={giveaway.prizes || []} selectedPrize={giveaway.selected_prize} completed={completed} />
        <EntryPanel
          session={session}
          entered={entered}
          viewerEntries={giveaway.viewer_entry_count || 0}
          balance={giveawayTokens}
          loading={tokensLoading}
          error={tokensError}
          cost={cost}
          totalCost={totalCost}
        />
      </div>

      {giveaway.winner && (
        <div className="mt-5 rounded border border-[hsl(var(--currency-event))]/60 bg-[hsl(var(--currency-event))]/10 p-4">
          <div className="label-eyebrow flex items-center gap-2 text-[hsl(var(--currency-event))]">
            <Trophy className="w-4 h-4" /> Winner picked
          </div>
          <div className="mt-2 text-xl font-semibold text-[hsl(var(--foreground))]">{giveaway.winner.display_name}</div>
          <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">@{giveaway.winner.username}</div>
          {giveaway.selected_prize && (
            <div className="mt-2 text-sm text-[hsl(var(--foreground))]">Prize: {giveaway.selected_prize}</div>
          )}
        </div>
      )}

      {!completed && (
        <div className="mt-6 rounded border border-[hsl(var(--currency-event))]/45 bg-[hsl(var(--currency-event))]/10 p-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            {session && (
              <label className="block text-sm">
                <span className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">Entries to add</span>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={entryCount}
                  onChange={(event) => onEntryCountChange(event.target.value)}
                  className="w-32 h-12 px-3 rounded bg-black/55 border border-[hsl(var(--border))] text-sm text-[hsl(var(--foreground))] outline-none focus:border-[hsl(var(--currency-event))]"
                />
              </label>
            )}
            <button
              type="button"
              onClick={onEnter}
              disabled={disabled}
              className="inline-flex justify-center items-center gap-2 px-5 py-3 rounded bg-[hsl(var(--currency-event))] text-white font-medium hover:brightness-110 transition disabled:bg-black/50 disabled:border disabled:border-[hsl(var(--border))] disabled:text-[hsl(var(--muted-foreground))] disabled:cursor-not-allowed"
            >
              {entering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
              {buttonLabel}
            </button>
            <span className="text-xs leading-relaxed text-[hsl(var(--muted-foreground))] max-w-xl">
              This page only shows the current giveaway. Free entry methods may also be announced during eligible giveaways.
            </span>
          </div>
        </div>
      )}
    </article>
  );
}

function EntryPanel({ session, entered, viewerEntries, balance, loading, error, cost, totalCost }) {
  return (
    <div className="rounded border border-[hsl(var(--currency-event))]/50 bg-[hsl(var(--currency-event))]/10 p-4">
      <div className="label-eyebrow text-[hsl(var(--currency-event))]">Your entry</div>
      {session ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <InfoStat label="Your entries" value={viewerEntries} />
            <InfoStat label="Tokens" value={loading ? "..." : Number(balance || 0)} />
            <InfoStat label="Each entry" value={cost > 0 ? `${cost} tokens` : "Free"} />
            <InfoStat label="This entry" value={totalCost > 0 ? `${totalCost} tokens` : "Free"} />
          </div>
          <p className={`mt-2 text-xs leading-relaxed ${error ? "text-red-100" : "text-[hsl(var(--muted-foreground))]"}`}>
            {error || (entered ? "You are entered. Add more entries if you want to spend more Giveaway Tokens." : "Enter while the giveaway is open. Free methods may be announced during stream.")}
          </p>
        </>
      ) : (
        <>
          <div className="mt-2 text-2xl font-black text-[hsl(var(--foreground))]">Sign in to enter</div>
          <p className="mt-2 text-xs leading-relaxed text-[hsl(var(--muted-foreground))]">
            Twitch sign-in shows your entries and Giveaway Token balance.
          </p>
        </>
      )}
    </div>
  );
}

function InfoStat({ label = "", value } = {}) {
  return (
    <div className="rounded border border-[hsl(var(--currency-event))]/25 bg-black/35 p-3">
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="mt-1 text-lg font-black text-[hsl(var(--foreground))]">{value}</div>
    </div>
  );
}

function PrizePreview({ prizes, selectedPrize, completed }) {
  if (!prizes.length) {
    return (
      <div className="rounded border border-[hsl(var(--border))] bg-black/45 p-4">
        <div className="label-eyebrow text-[hsl(var(--currency-event))]">Prizes</div>
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
          Prize details will show here once Parker adds them.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded border border-[hsl(var(--border))] bg-black/45 p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="label-eyebrow text-[hsl(var(--currency-event))]">Prize wheel</div>
          <h4 className="mt-1 text-xl font-semibold text-[hsl(var(--foreground))]">Possible prizes</h4>
        </div>
        <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{prizes.length} items</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {prizes.map((prize, index) => {
          const won = completed && selectedPrize && selectedPrize === prize;
          return (
            <div
              key={`${prize}-${index}`}
              className={`rounded border px-3 py-2 text-sm ${
                won
                  ? "border-[hsl(var(--currency-event))] bg-[hsl(var(--currency-event))]/15 text-[hsl(var(--foreground))]"
                  : "border-[hsl(var(--border))] bg-black/35 text-[hsl(var(--muted-foreground))]"
              }`}
            >
              <span className="text-[10px] uppercase tracking-widest mr-2">#{index + 1}</span>
              <span className={won ? "font-semibold" : ""}>{prize}</span>
              {won && <span className="ml-2 text-[10px] uppercase tracking-widest">Won</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyGiveaways() {
  return (
    <div className="surface bg-black/75 p-6 sm:p-7 text-sm text-[hsl(var(--muted-foreground))]">
      <Gift className="w-8 h-8 text-[hsl(var(--currency-event))] mb-3" strokeWidth={1.5} />
      <h3 className="text-xl font-semibold tracking-tight text-[hsl(var(--foreground))]">No giveaway is active right now.</h3>
      <p className="mt-2 max-w-2xl leading-relaxed">
        The giveaway page will light up when Parker opens the next one. Check back during stream or join Discord for giveaway announcements.
      </p>
      <div className="mt-5">
        <PublicLinkButton href={PUBLIC_LINKS.discord}>
          <MessageSquare className="w-4 h-4" /> Join Discord
        </PublicLinkButton>
      </div>
    </div>
  );
}

async function fetchViewerBalances(session) {
  const viewerId = session?.twitch_user_id || session?.platform_user_id;
  if (!viewerId) return { balances: {} };
  const { data } = await api.get(`/currency/viewers/${encodeURIComponent(viewerId)}/balances`, {
    params: {
      login: session?.login || session?.display_name || viewerId,
    },
  });
  return data;
}
